import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

const styles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    position: 'relative',
    width: '22rem',
    background: 'linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    [theme.breakpoints.up("xl")]: {
      width: '26.5rem',
      padding: '1.8rem',
    },
  },
  animateIn: {
    animation: '$scaleInBounce 0.5s ease-in-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  icon: {
    fontSize: '3rem',
    color: '#A52A2A',
    filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.6))',
    textShadow: '0 0 15px rgba(255, 255, 255, 0.8)',
    marginBottom: '1rem',
    [theme.breakpoints.up("xl")]: {
      fontSize: '3.8rem',
      marginBottom: '1.5rem',
    },
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 525,
    color: '#ffffff',
    textAlign: 'center',
    [theme.breakpoints.up("xl")]: {
      fontSize: '1.5rem',
    },
  },
  subtitle: {
    fontSize: '1rem',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '1.5rem',
    [theme.breakpoints.up("xl")]: {
      fontSize: '1.2rem',
      marginBottom: '1.8rem',
    },
  },
  actions: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '1rem',
    [theme.breakpoints.up("xl")]: {
      gap: '1.4rem',
    },
  },
  cancelButton: {
    padding: '0.4rem 2rem',
    backgroundColor: '#808080',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#666666',
    },
    [theme.breakpoints.up("xl")]: {
      padding: '0.45rem 2.4rem',
      fontSize: '1.2rem',
    },
  },
  confirmButton: {
    padding: '0.4rem 2rem',
    backgroundColor: '#A52A2A',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#8B0000',
    },
    [theme.breakpoints.up("xl")]: {
      padding: '0.45rem 2.4rem',
      fontSize: '1.2rem',
    },
  },
  "@keyframes scaleInBounce": {
    '0%': { transform: 'scale(0.5)', opacity: 0 },
    '25%': { transform: 'scale(1.1)', opacity: 0.6 },
    '50%': { transform: 'scale(0.95)', opacity: 0.8 },
    '75%': { transform: 'scale(1.02)', opacity: 0.9 },
    '100%': { transform: 'scale(1)', opacity: 1 },
  },
});

function ConfirmDialog({
  classes,
  open,
  IconComponent,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div className={classes.overlay}>
      <div role="dialog" className={`${classes.content} ${classes.animateIn}`}>
        <div className={classes.header}>
          {IconComponent ? (
            <IconComponent className={classes.icon} />
          ) : null}
          <div>
            <Typography className={classes.title}>
              {title}
            </Typography>
            {message && (
              <Typography className={classes.subtitle}>
                {message}
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.actions}>
          <Button className={classes.cancelButton} onClick={onCancel}>
            {cancelText}
          </Button>
          <Button className={classes.confirmButton} onClick={onConfirm}>
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

ConfirmDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  IconComponent: PropTypes.elementType,
  title: PropTypes.node,
  message: PropTypes.node,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default withStyles(styles)(ConfirmDialog);


